import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import {
  Search, Users, Loader2, SlidersHorizontal, Clock, Flame,
  Music, Disc3, Mic, Video, Palette, Settings, Building2, Radio, Headphones, MapPin, Briefcase,
  Rocket, Sparkles, UserPlus, TrendingUp, ChevronRight, Star
} from 'lucide-react';
import { API } from '../config/api';
import { LiveBadge, LiveDot } from '../components/LiveBadge';

const ROLE_ICONS = {
  artist: Music, producer: Disc3, podcaster: Mic, videographer: Video,
  designer: Palette, engineer: Settings, studio: Building2, streamer: Radio, listener: Headphones,
};
const ROLE_COLORS = {
  artist: 'from-violet-600/20 to-purple-600/10 border-violet-500/30 text-violet-400',
  producer: 'from-orange-600/20 to-amber-600/10 border-orange-500/30 text-orange-400',
  podcaster: 'from-emerald-600/20 to-green-600/10 border-emerald-500/30 text-emerald-400',
  videographer: 'from-sky-600/20 to-cyan-600/10 border-sky-500/30 text-sky-400',
  designer: 'from-pink-600/20 to-rose-600/10 border-pink-500/30 text-pink-400',
  engineer: 'from-amber-600/20 to-yellow-600/10 border-amber-500/30 text-amber-400',
  studio: 'from-indigo-600/20 to-blue-600/10 border-indigo-500/30 text-indigo-400',
  streamer: 'from-red-600/20 to-orange-600/10 border-red-500/30 text-red-400',
};
const CONTENT_LABELS = {
  tracks: 'tracks', beats: 'beats', episodes: 'episodes',
  portfolio: 'works', services: 'services', streams: 'streams',
};
const GENRES = ['Hip-Hop', 'R&B', 'Trap', 'Pop', 'Afrobeats', 'Go-Go', 'Drill', 'Lo-Fi', 'Soul', 'Dance', 'Electronic'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', icon: Clock },
  { value: 'trending', label: 'Trending', icon: Flame },
  { value: 'popular', label: 'Most Followed', icon: Users },
];

function CreativeCard({ creative }) {
  const Icon = ROLE_ICONS[creative.role] || Music;
  const colorClass = ROLE_COLORS[creative.role] || ROLE_COLORS.artist;
  const badgeParts = colorClass.split(' ');
  const badgeClasses = badgeParts.filter(c => c.startsWith('border-') || c.startsWith('text-')).join(' ');
  const bgClasses = badgeParts.filter(c => c.startsWith('from-') || c.startsWith('to-')).join(' ');
  const contentLabel = CONTENT_LABELS[creative.content_type] || '';

  return (
    <Link to={`/creative/${creative.handle}`} data-testid={`creative-card-${creative.user_id}`}>
      <Card className="group bg-muted/20 border-border hover:border-white/[0.15] transition-all duration-300 overflow-hidden h-full">
        <div className="p-4 flex flex-col items-center text-center">
          {creative.avatar_url ? (
            <img src={creative.avatar_url} alt={creative.name}
              className="rounded-2xl object-cover mb-3 group-hover:scale-105 transition-transform border-2 border-border"
              style={{ width: 104, height: 104 }} />
          ) : (
            <div className={`rounded-2xl bg-gradient-to-br ${bgClasses} flex items-center justify-center mb-3 border-2 border-border`} style={{ width: 104, height: 104 }}>
              <Icon className="w-10 h-10 opacity-60" />
            </div>
          )}
          <h3 className="font-semibold text-sm text-foreground truncate w-full">{creative.name}</h3>
          <p className="text-[11px] text-muted-foreground truncate w-full">{creative.handle ? `@${creative.handle}` : ""}</p>
          <Badge className={`mt-1.5 text-[10px] font-medium bg-transparent border ${badgeClasses}`}>
            <Icon className="w-3 h-3 mr-1" />{creative.role_label}
          </Badge>
          <LiveBadge userId={creative.user_id} size="xs" className="mt-1" />
          {creative.location && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1.5">
              <MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{creative.location}</span>
            </p>
          )}
          {creative.genres?.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center mt-2">
              {creative.genres.slice(0, 2).map((g, i) => (
                <span key={i} className="text-[10px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded">{g}</span>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-2.5">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{creative.followers_count || 0}</span>
            {contentLabel && <span className="flex items-center gap-1"><Icon className="w-3 h-3" />{creative.content_count || 0} {contentLabel}</span>}
          </div>
          {creative.work_with_me && (
            <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] mt-2">
              <Briefcase className="w-3 h-3 mr-1" />Available
            </Badge>
          )}
        </div>
      </Card>
    </Link>
  );
}

export default function FindCreativesPage() {
  const [creatives, setCreatives] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeRole, setActiveRole] = useState('');
  const [genre, setGenre] = useState('');
  const [sort, setSort] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchCreatives(); }, [activeRole, genre, sort]);

  const fetchCreatives = async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '40', sort });
      if (activeRole) params.set('role', activeRole);
      if (genre) params.set('genre', genre);
      if (q || search) params.set('search', q || search);
      const res = await fetch(`${API}/creatives?${params}`);
      if (res.ok) {
        const data = await res.json();
        setCreatives(data.creatives || []);
        if (data.roles) setRoles(data.roles);
      }
    } catch {} finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); fetchCreatives(search); };
  const RoleIcon = ROLE_ICONS[activeRole] || Users;

  return (
    <div className="min-h-screen pb-20" data-testid="find-creatives-page">
      {/* Header */}
      <div className="relative py-10 md:py-14 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-800/10 to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto relative">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1.5" data-testid="page-title">
            Discover Creatives
          </h1>
          <p className="text-muted-foreground text-sm mb-6 max-w-lg">
            Find artists, producers, engineers, and more in the DMV. Browse, connect, and collaborate.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mb-5">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="search-input" placeholder="Search by name, genre, skill..."
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-card/80 border-border h-10" />
            </div>
            <Button type="submit" size="sm" className="bg-orange-600 hover:bg-orange-700 h-10 px-4">Search</Button>
            <Button type="button" variant="outline" size="icon" className="border-border h-10 w-10"
              onClick={() => setShowFilters(!showFilters)}>
              <SlidersHorizontal className="w-4 h-4" />
            </Button>
          </form>

          {/* Role Tabs */}
          <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide" data-testid="role-tabs">
            <Button size="sm" variant={!activeRole ? 'default' : 'ghost'}
              className={!activeRole ? 'bg-foreground text-background hover:bg-foreground/90' : 'text-muted-foreground hover:text-foreground'}
              onClick={() => setActiveRole('')}>
              <Users className="w-3.5 h-3.5 mr-1.5" />All
            </Button>
            {Object.entries(roles).map(([key, label]) => {
              const Icon = ROLE_ICONS[key] || Music;
              const isActive = activeRole === key;
              return (
                <Button key={key} size="sm" variant={isActive ? 'default' : 'ghost'}
                  className={isActive ? 'bg-foreground text-background hover:bg-foreground/90' : 'text-muted-foreground hover:text-foreground'}
                  onClick={() => setActiveRole(isActive ? '' : key)}
                  data-testid={`role-tab-${key}`}>
                  <Icon className="w-3.5 h-3.5 mr-1.5" />{label}
                </Button>
              );
            })}
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="mt-3 p-4 rounded-xl bg-card/60 border border-border max-w-lg" data-testid="filters-panel">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Genre</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                <Badge className={`cursor-pointer text-xs ${!genre ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  onClick={() => setGenre('')}>All</Badge>
                {GENRES.map(g => (
                  <Badge key={g}
                    className={`cursor-pointer text-xs ${genre === g ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                    onClick={() => setGenre(genre === g ? '' : g)}>{g}</Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Sort</p>
              <div className="flex gap-2">
                {SORT_OPTIONS.map(s => (
                  <Button key={s.value} variant={sort === s.value ? 'default' : 'outline'} size="sm"
                    className={sort === s.value ? 'bg-orange-600 hover:bg-orange-700' : 'border-border text-muted-foreground'}
                    onClick={() => setSort(s.value)}>
                    <s.icon className="w-3.5 h-3.5 mr-1" />{s.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-4">
        {/* Discovery Sections — show when no search/filter active */}
        {!search && !activeRole && !genre && <DiscoverySections />}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>
        ) : creatives.length === 0 ? (
          <div className="text-center py-16" data-testid="no-results">
            <RoleIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
            <p className="text-muted-foreground mb-1">No creatives found</p>
            <p className="text-muted-foreground/80 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="creatives-grid">
            {creatives.map(c => <CreativeCard key={c.user_id} creative={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Discovery Sections ──────────────────────────────────
function DiscoverySections() {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [rising, setRising] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [newCreators, setNewCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API}/discover/creator-suggestions?category=rising&limit=8`, { headers }).then(r => r.ok ? r.json() : { creators: [] }).catch(() => ({ creators: [] })),
      fetch(`${API}/discover/creator-suggestions?category=similar&limit=8`, { headers }).then(r => r.ok ? r.json() : { creators: [] }).catch(() => ({ creators: [] })),
      fetch(`${API}/discover/creator-suggestions?category=new&limit=6`, { headers }).then(r => r.ok ? r.json() : { creators: [] }).catch(() => ({ creators: [] })),
    ]).then(([r, s, n]) => {
      setRising(r.creators || []);
      setSimilar(s.creators || []);
      setNewCreators(n.creators || []);
    }).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const handleFollow = async (userId) => {
    try {
      await fetch(`${API}/users/${userId}/follow`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const remove = (prev) => prev.filter(c => c.user_id !== userId);
      setRising(remove);
      setSimilar(remove);
      setNewCreators(remove);
    } catch {}
  };

  if (loading || (!rising.length && !similar.length && !newCreators.length)) return null;

  return (
    <div className="mb-8 space-y-6" data-testid="discovery-sections">
      {rising.length > 0 && (
        <DiscoveryRow title="Rising Creators" subtitle="Making waves in the DMV" icon={Rocket} iconColor="text-orange-400" creators={rising} onFollow={handleFollow} testId="rising-creators" />
      )}
      {similar.length > 0 && (
        <DiscoveryRow title="Similar to Who You Like" subtitle="Based on your taste" icon={Sparkles} iconColor="text-violet-400" creators={similar} onFollow={handleFollow} testId="similar-creators" />
      )}
      {newCreators.length > 0 && (
        <DiscoveryRow title="Just Joined" subtitle="New creators this week" icon={Star} iconColor="text-cyan-400" creators={newCreators} onFollow={handleFollow} badge="NEW" testId="new-creators" />
      )}
    </div>
  );
}

function DiscoveryRow({ title, subtitle, icon: Icon, iconColor, creators, onFollow, badge, testId }) {
  return (
    <div data-testid={`discovery-${testId}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <div>
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-foreground">{title}</h3>
          <p className="text-[11px] text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2">
        {creators.map(c => (
          <div key={c.user_id} className="flex-shrink-0 w-36 group">
            <Link to={`/u/${c.handle || c.user_id}`}>
              <div className="relative">
                <div className="w-full aspect-square rounded-2xl bg-muted overflow-hidden mb-2 group-hover:ring-2 ring-violet-500/40 transition-all">
                  {c.avatar ? (
                    <img src={c.avatar} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-muted-foreground/50 bg-gradient-to-br from-muted/50 to-muted">
                      {(c.name || '?')[0]}
                    </div>
                  )}
                </div>
                {badge && <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-500 text-white uppercase">{badge}</span>}
              </div>
            </Link>
            <p className="text-xs font-semibold truncate text-foreground">{c.name}</p>
            <p className="text-[10px] text-muted-foreground truncate mb-1.5">@{c.handle || c.user_id}</p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Users className="w-3 h-3" /> {c.followers || 0}</span>
              <button onClick={(e) => { e.preventDefault(); onFollow(c.user_id); }} data-testid={`discover-follow-${c.user_id}`}
                className="text-[10px] px-2.5 py-0.5 rounded-full bg-violet-600 hover:bg-violet-500 text-white font-semibold transition-colors">
                Follow
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
